import type { CapabilityState, FeatureFlags } from '@buakei/types';
import { Badge, ErrorState, LoadingState } from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, FileSearch, PenLine, Search, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/services/api';
import { AssistantHome } from '@/shell/AssistantHome';

const FLAG_LABELS: Record<keyof FeatureFlags, string> = {
  authentication: 'Sign-in',
  legalResearch: 'Legal research',
  aiAssistant: 'Ask Buakei',
  drafting: 'Drafting',
  documentUploads: 'Document uploads',
  learningTutor: 'Learning tutor',
  editorialDecisions: 'Editorial decisions',
  payments: 'Payments',
  exports: 'Exports',
};

const STATE_PRESENTATION: Record<CapabilityState, { label: string; tone: 'success' | 'plain' }> = {
  live: { label: 'Live', tone: 'success' },
  demo: { label: 'Demo only', tone: 'plain' },
  preview: { label: 'Preview', tone: 'plain' },
  unavailable: { label: 'Not available', tone: 'plain' },
};

/**
 * "What is and isn't operational", read from the API so the UI cannot overstate it.
 * A quiet disclosure on the home page (ADR-009); it loads only when opened.
 */
export function CapabilityStatus() {
  const [open, setOpen] = useState(false);
  const flags = useQuery({
    queryKey: ['feature-flags'],
    queryFn: api.featureFlags,
    enabled: open,
  });
  return (
    <details
      className="group w-full max-w-md text-left"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="mx-auto flex w-fit cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-navy-900 hover:underline [&::-webkit-details-marker]:hidden">
        What works today
        <ChevronDown className="size-4 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <div className="mt-3 rounded-lg border border-line-200 bg-white p-4">
        {flags.isPending ? <LoadingState label="Loading capability status" /> : null}
        {flags.isError ? (
          <ErrorState title="Capability status is unavailable" onRetry={() => void flags.refetch()}>
            Treat every feature as a demo until this loads.
          </ErrorState>
        ) : null}
        {flags.data ? (
          <ul className="grid gap-2 sm:grid-cols-2">
            {(Object.keys(FLAG_LABELS) as (keyof FeatureFlags)[]).map((key) => {
              const p = STATE_PRESENTATION[flags.data[key]];
              return (
                <li key={key} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-ink-900">{FLAG_LABELS[key]}</span>
                  <Badge tone={p.tone}>{p.label}</Badge>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </details>
  );
}

/** Professional home (ADR-009): minimal and assistant-first. */
export function ProfessionalDashboard() {
  return (
    <AssistantHome
      title="Professional"
      question="What do you want to do today?"
      actions={[
        { to: '/professional/research', label: 'Research', icon: Search },
        { to: '/professional/documents', label: 'Review', icon: FileSearch, note: 'Preview' },
        { to: '/professional/drafting', label: 'Draft', icon: PenLine },
      ]}
      trustIcon={ShieldCheck}
      trust="Private to your workspace. Every answer links to its sources."
    >
      <CapabilityStatus />
    </AssistantHome>
  );
}
